// Drag-and-drop orchestration for the Organize view.
// @ts-check
import { useSensor, useSensors } from '@dnd-kit/core';
import { CustomPointerSensor } from '@/sensors/CustomPointerSensor';
import { useTaskDragAndDrop } from '@/features/tasks/hooks/list/use-list-dnd.js';
import { useTaskMutationsContext } from '@/features/tasks/context/useTaskMutationsContext';
import {
  resolveNewParentId,
  computeSubtaskOrderKey,
  computeReorderOrderKey,
  isCircularDrop,
  buildDropUpdatePayload,
} from '@/features/tasks/utils/interactions/dndUtils';
import { resolveDndTaskId } from '@/features/tasks/utils/interactions/dragHandlers';
import { translate } from '@/i18n/useT';

const SUBTASK_MOVE_WARNING_THRESHOLD = 10;

function countDescendants(taskId, tasks) {
  const children = tasks.filter((t) => t.parentId === taskId);
  return children.reduce(
    (sum, child) => sum + 1 + countDescendants(child.id, tasks),
    0,
  );
}

const checkDescendantCountWarning = (activeId, allTasks) => {
  const descendantCount = countDescendants(activeId, allTasks);
  if (descendantCount > SUBTASK_MOVE_WARNING_THRESHOLD) {
    const confirmed = window.confirm(
      translate(
        'organize.confirmMoveWithDescendants',
        'This task has {count} subtasks. Are you sure you want to move it?',
        { count: String(descendantCount) },
      ),
    );
    return confirmed === false;
  }
  return false;
};

/**
 * @param {boolean} nesting
 * @param {DndOver} over
 * @param {string|number} activeId
 * @param {Function} updateTask
 * @param {Task[]} allTasks
 * @param {AppUser} user
 */
const handleClearDateAndTime = (
  nesting,
  over,
  activeId,
  updateTask,
  allTasks,
  user,
) => {
  if (!nesting) return;
  const overTask = allTasks.find((t) => t.id === over.id);
  if (overTask && (overTask.date || overTask.time)) {
    updateTask(over.id, { date: null, time: null }, user.uid);
  }
};

/**
 * @param {boolean} nesting
 * @param {string | null} newParentId
 * @param {DndActive} active
 * @param {DndOver} over
 * @param {Task[]} allTasks
 * @returns {string}
 */
const calculateNewOrderKey = (nesting, newParentId, active, over, allTasks) => {
  return nesting
    ? computeSubtaskOrderKey(newParentId, allTasks)
    : computeReorderOrderKey(active, over, newParentId, allTasks);
};

const findActiveTask = (activeId, allTasks) => {
  return allTasks.find((t) => t.id === activeId);
};

/**
 * @param {DndActive} active
 * @param {DndOver | null} over
 * @param {Task[]} allTasks
 * @returns {boolean}
 */
const shouldSkipDrop = (active, over, allTasks) => {
  if (!over || active.id === over.id) return true;

  const activeTask = findActiveTask(active.id, allTasks);
  if (!activeTask) {
    console.error('[DragEnd] Active task not found!', { activeId: active.id });
    return true;
  }

  if (checkDescendantCountWarning(String(active.id), allTasks)) return true;

  return false;
};

const shouldCancelDropDueToCircularReference = (
  activeTask,
  newParentId,
  allTasks,
) => {
  if (isCircularDrop(activeTask, newParentId, allTasks)) {
    console.error(
      '[DragEnd] CANCELLED: Cannot move a task into one of its own descendants.',
    );
    return true;
  }
  return false;
};

/**
 * @param {Task} activeTask
 * @param {DndActive} active
 * @param {DndOver} over
 * @param {Task[]} allTasks
 * @returns {string | null}
 */
const determineNewParentId = (activeTask, active, over, allTasks) => {
  const sameContainer = () => {
    const activeContainer = active.data?.current?.sortable?.containerId;
    const overContainer = over.data?.current?.sortable?.containerId;
    return !!(
      activeContainer &&
      overContainer &&
      activeContainer === overContainer
    );
  };

  return sameContainer()
    ? (activeTask.parentId ?? null)
    : resolveNewParentId(active, over, allTasks);
};

/**
 * Encapsulates all drag-and-drop logic for the OrganizePage.
 * Configures sensors, tracks the active drag item, and handles the drop.
 * @param {Task[]} allTasks
 * @param {AppUser} user
 */
export function useOrganizeDragAndDrop(allTasks, user) {
  const { updateTask } = useTaskMutationsContext();
  const { activeItem, handleDragStart, handleDragCancel } =
    useTaskDragAndDrop(allTasks);

  const pointerSensor = useSensor(CustomPointerSensor, {
    activationConstraint: { distance: 8 },
  });

  const sensors = useSensors(pointerSensor);

  /** @param {import('@dnd-kit/core').DragStartEvent} event */
  const handleDragStartInternal = (event) => {
    document.body.classList.add('dnd-is-dragging');
    handleDragStart(event);
  };

  const handleDragCancelInternal = () => {
    document.body.classList.remove('dnd-is-dragging');
    handleDragCancel();
  };

  /**
   * @param {DndActive} active
   * @param {DndOver} over
   */
  const processDragEnd = (active, over) => {
    if (shouldSkipDrop(active, over, allTasks)) return null;

    const activeTask = findActiveTask(active.id, allTasks);
    const newParentId = determineNewParentId(
      activeTask,
      active,
      over,
      allTasks,
    );
    // Nesting is whatever actually changes parentage — deriving it from the
    // resolved parent keeps the orderKey path and the date/time clearing in
    // step with the drop that was really performed.
    const nesting = (newParentId ?? null) !== (activeTask.parentId ?? null);

    if (
      shouldCancelDropDueToCircularReference(activeTask, newParentId, allTasks)
    )
      return null;

    handleClearDateAndTime(
      nesting,
      over,
      active.id,
      updateTask,
      allTasks,
      user,
    );

    const newOrderKey = calculateNewOrderKey(
      nesting,
      newParentId,
      active,
      over,
      allTasks,
    );
    const updatePayload = buildDropUpdatePayload(
      activeTask,
      newParentId,
      newOrderKey,
      allTasks,
    );

    if (Object.keys(updatePayload).length > 0) {
      updateTask(active.id, updatePayload, user.uid);
    }

    return true;
  };

  /** @param {DndEvent} event */
  const handleDragEnd = (event) => {
    document.body.classList.remove('dnd-is-dragging');
    handleDragCancel();
    if (event.over) {
      // dnd-kit ids get list-prefixed (e.g. `subtasks-of-<id>:<taskId>`) when a
      // task is rendered in more than one list — resolve to the real task id
      // before matching against `allTasks` below.
      const active = {
        ...event.active,
        id: resolveDndTaskId(event.active) ?? event.active.id,
      };
      const over = { ...event.over, id: resolveDndTaskId(event.over) ?? event.over.id };
      processDragEnd(active, over);
    }
  };

  return {
    activeItem,
    handleDragStart: handleDragStartInternal,
    handleDragEnd,
    handleDragCancel: handleDragCancelInternal,
    sensors,
  };
}
